/**
 * Sensitive-field redaction for event properties.
 *
 * Scrubs well-known sensitive key names, keys matching a credentials/secret/token
 * regex, and URL-shaped values (query params + path PII). Used by Contextune
 * before pushing any captured event into the ring buffer.
 */

import {
  sanitiseForLLM,
  SANITISE_DEFAULT_LIMIT,
  SANITISE_URL_LIMIT,
} from '../sanitise/forLLM.js';
import { redactUrl } from '../sanitise/url.js';

const SENSITIVE_NAME_KEYS = new Set<string>([
  'password',
  'passwd',
  'pwd',
  'email',
  'credit_card',
  'creditcard',
  'cc_number',
  'cvv',
  'ssn',
  'phone',
  'phone_number',
  'dob',
  'date_of_birth',
  'address',
  'street',
  'postcode',
  'zip',
  'auth',
  'authorization',
  'bearer',
  'cookie',
  'set_cookie',
]);

const SENSITIVE_REGEX = /(password|secret|token|api[_-]?key|jwt|session)/i;

/** Keys whose string values are treated as URLs for value-scrubbing. */
const URL_LIKE_KEYS = new Set<string>(['url', 'href', 'uri', 'referrer', 'referer']);

/** Bound recursion to keep redaction work O(payload size). */
const MAX_REDACT_DEPTH = 4;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function redactValue(value: unknown, key: string, depth: number): unknown {
  if (typeof value === 'string') {
    if (URL_LIKE_KEYS.has(key.toLowerCase())) {
      return sanitiseForLLM(redactUrl(value, SANITISE_URL_LIMIT), SANITISE_URL_LIMIT);
    }
    return sanitiseForLLM(value, SANITISE_DEFAULT_LIMIT);
  }
  if (Array.isArray(value)) {
    if (depth >= MAX_REDACT_DEPTH) return value;
    return value.map((v) => redactValue(v, key, depth + 1));
  }
  if (isPlainObject(value)) {
    if (depth >= MAX_REDACT_DEPTH) return value;
    return redactObject(value, depth + 1);
  }
  return value;
}

function redactObject(
  input: Record<string, unknown>,
  depth: number
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (k === '__proto__' || k === 'constructor' || k === 'prototype') {
      continue;
    }
    if (SENSITIVE_NAME_KEYS.has(k.toLowerCase())) {
      out[k] = '[REDACTED]';
      continue;
    }
    if (SENSITIVE_REGEX.test(k) && typeof v === 'string') {
      out[k] = '[REDACTED]';
      continue;
    }
    out[k] = redactValue(v, k, depth);
  }
  return out;
}

export function redactProperties(
  input: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!input) return undefined;
  return redactObject(input, 0);
}
